/**
 * `useAuthedFetch` — provide the token-attaching `fetch` to the data layer.
 *
 * This hook is the React seam between the provider's {@link TokenClient}
 * (sourced through the `@viamericas/auth-react` adapter, task 3.1) and the pure
 * {@link createAuthedFetch} wrapper (task 3.5). It builds the wrapper **once**
 * (memoized against the token client) and returns a value with the exact
 * `typeof fetch` shape, so it drops directly into the existing injectable
 * `fetchFn` slots — `useRunLoader(selection, { fetchFn })`, `RepoDetail`'s
 * `fetchFn`, and `fetchRunsCatalog(signal, basePath, fetchFn)` (Req 3.1–3.4).
 *
 * Stable identity matters: those consumers put `fetchFn` in effect/dependency
 * arrays, so a fresh function on every render would re-trigger loads. The
 * returned reference therefore changes only when the underlying token client
 * changes (which the adapter already keeps render-stable), never when a caller
 * passes fresh inline handler objects — those are read through a ref.
 *
 * Terminal authorization outcomes are routed via {@link AuthedFetchHandlers}:
 *
 *  - `onUnauthenticated` (persistent 401 after one refresh+retry, Req 4.2):
 *    defaults to beginning interactive sign-in through the token client
 *    (`tokens.signIn()`), which returns the user to authentication. This keeps
 *    the hook decoupled from any router or global store.
 *  - `onAccessDenied` (403 from the edge, Req 5.3): defaults to a no-op
 *    placeholder. The access-denied **screen** is rendered by the `RoleGate`
 *    fallback set up in task 3.12, and task 3.13 implements that screen plus the
 *    state plumbing to surface a *runtime* 403. When that wiring lands, the app
 *    wrapper passes a real `onAccessDenied` (e.g. one that flips an
 *    access-denied state or navigates) via the optional `handlers` argument
 *    below — no change to this hook is required.
 *
 * Requirements: 3.1, 4.2, 5.3.
 */

import { useMemo, useRef } from "react";

import { useTokenClient } from "./adapters";
import {
  createAuthedFetch,
  type AuthedFetchHandlers,
} from "./authedFetch";

/**
 * Optional overrides for the terminal-outcome handlers. Both are optional so
 * the hook satisfies the plain `useAuthedFetch()` call site today; task 3.12/13
 * can supply real callbacks once the access-denied screen state exists.
 */
export type UseAuthedFetchHandlers = Partial<AuthedFetchHandlers>;

/**
 * Build the memoized, token-attaching `fetch` for the data layer.
 *
 * @param handlers Optional overrides for `onUnauthenticated`/`onAccessDenied`.
 *   When omitted, `onUnauthenticated` begins interactive sign-in via the token
 *   client and `onAccessDenied` is a documented no-op (see module docs).
 * @returns A `typeof fetch` with a stable identity across renders.
 */
export function useAuthedFetch(
  handlers: UseAuthedFetchHandlers = {},
): typeof fetch {
  const tokens = useTokenClient();

  // Hold the latest caller-supplied handlers in a ref. The memoized fetch reads
  // `handlersRef.current` at invocation time, so callers may pass fresh inline
  // objects on every render without destabilizing the returned fetch identity.
  const handlersRef = useRef<UseAuthedFetchHandlers>(handlers);
  handlersRef.current = handlers;

  return useMemo<typeof fetch>(() => {
    const resolvedHandlers: AuthedFetchHandlers = {
      onUnauthenticated: () => {
        const override = handlersRef.current.onUnauthenticated;
        if (override) {
          override();
          return;
        }
        // Default: a persistent 401 returns the user to interactive sign-in
        // (Req 4.2). `signIn()` performs a redirect; ignore the promise here.
        void tokens.signIn();
      },
      onAccessDenied: () => {
        const override = handlersRef.current.onAccessDenied;
        if (override) {
          override();
          return;
        }
        // Default: no-op. The access-denied screen is rendered by the RoleGate
        // fallback (task 3.12); surfacing a *runtime* 403 is wired in task 3.13
        // by passing an `onAccessDenied` override from the app wrapper.
      },
    };

    // Memoized on `tokens` only: the wrapper's identity is stable across renders
    // (handlers are read via the ref), so consumers' fetchFn dependency arrays
    // do not churn. `createAuthedFetch` defaults `baseFetch` to global `fetch`.
    return createAuthedFetch(tokens, resolvedHandlers);
  }, [tokens]);
}
